/**
 * StoryboardView — the Storyboard tab.
 *
 * Phase 1: professional shot lists per scene (editable cards, autosave, PDF).
 * Phase 2: B&W pencil panels — character anchor portraits ("Cast") keep faces
 * consistent; panels generate per scene in the background and fill in as they
 * finish; any panel can be redrawn with a director's tweak note.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useIsMobile } from "@/lib/useMediaQuery";
import {
  Clapperboard, Loader2, RefreshCw, FileDown, Plus, Trash2, ChevronUp, ChevronDown,
  ImageOff, AlertTriangle, Users, ImagePlus, X, User, List, LayoutGrid,
} from "lucide-react";

const SHOT_TYPES = ["EWS", "WS", "FULL", "MS", "MCU", "CU", "ECU", "OTS", "POV", "TWO-SHOT", "INSERT"];
const CAMERA_MOVES = ["STATIC", "PAN L", "PAN R", "TILT UP", "TILT DOWN", "DOLLY IN", "DOLLY OUT",
  "TRACK L", "TRACK R", "CRANE UP", "CRANE DOWN", "HANDHELD", "PUSH IN", "ZOOM IN", "STEADICAM"];

const imgSrc = (scriptId, imageId) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/scripts/${scriptId}/storyboard/image/${imageId}`;

function shotLetter(i) {
  let s = "", n = i + 1;
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

export function StoryboardView({ scriptId, canEdit, scriptTitle }) {
  const confirm = useConfirm();
  const isMobile = useIsMobile();
  const [scenes, setScenes] = useState([]);
  const [scenesLoading, setScenesLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [castOpen, setCastOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {src, caption}
  const [view, setView] = useState(() => localStorage.getItem("screenplay_sb_view") || "list");
  const [exportOpen, setExportOpen] = useState(false);
  const pollRef = useRef(null);
  const saveTimer = useRef(null);
  const shotsRef = useRef(null);

  const loadScenes = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/storyboard`);
      setScenes(data.scenes || []);
      setSelectedId((cur) => cur || data.scenes?.[0]?.scene_id || null);
    } catch { /* ignore */ }
    finally { setScenesLoading(false); }
  }, [scriptId]);

  useEffect(() => { loadScenes(); }, [loadScenes]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const boardBusy = (b) =>
    b?.status === "generating" || (b?.shots || []).some((s) => s.panel_status === "generating");

  const startPolling = useCallback((sceneId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/scripts/${scriptId}/storyboard/${sceneId}`);
        setBoard(data.board);
        if (!boardBusy(data.board)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadScenes();
          if (data.board?.status === "error") toast.error(data.board.error || "Shot list failed.");
        }
      } catch { /* keep polling */ }
    }, 4500);
  }, [scriptId, loadScenes]);

  const loadBoard = useCallback(async (sceneId) => {
    setBoardLoading(true);
    try {
      const { data } = await api.get(`/scripts/${scriptId}/storyboard/${sceneId}`);
      setBoard(data.board);
      if (boardBusy(data.board)) startPolling(sceneId);
    } catch { setBoard(null); }
    finally { setBoardLoading(false); }
  }, [scriptId, startPolling]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (selectedId) loadBoard(selectedId);
  }, [selectedId, loadBoard]);

  const generate = async () => {
    try {
      await api.post(`/scripts/${scriptId}/storyboard/${selectedId}/generate`);
      setBoard((b) => ({ ...(b || { scene_id: selectedId, shots: [] }), status: "generating", error: null }));
      setScenes((prev) => prev.map((s) => (s.scene_id === selectedId ? { ...s, status: "generating" } : s)));
      startPolling(selectedId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the shot list.");
    }
  };

  const generatePanels = async () => {
    const missing = (board?.shots || []).filter((s) => s.panel_status !== "ready" && s.panel_status !== "generating").length;
    if (!missing) { toast("Every shot already has a panel — use a card's redraw button to redo one."); return; }
    const ok = await confirm({
      title: `Draw ${missing} panel${missing === 1 ? "" : "s"}?`,
      description: "Each panel is one image generation (roughly $0.04 of key balance). They'll appear as they finish — you can keep working.",
      confirmLabel: "Draw panels",
    });
    if (!ok) return;
    try {
      await api.post(`/scripts/${scriptId}/storyboard/${selectedId}/panels`);
      setBoard((b) => (b ? {
        ...b,
        shots: b.shots.map((s) => (s.panel_status !== "ready" ? { ...s, panel_status: "generating" } : s)),
      } : b));
      startPolling(selectedId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the panels.");
    }
  };

  const regenPanel = async (shotId, note) => {
    try {
      await api.post(`/scripts/${scriptId}/storyboard/${selectedId}/shots/${shotId}/panel`, { note: note || "" });
      setBoard((b) => (b ? {
        ...b,
        shots: b.shots.map((s) => (s.id === shotId ? { ...s, panel_status: "generating" } : s)),
      } : b));
      startPolling(selectedId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't redraw that panel.");
    }
  };

  const scheduleSave = (shots) => {
    shotsRef.current = shots;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/scripts/${scriptId}/storyboard/${selectedId}`, { shots: shotsRef.current });
        setBoard((b) => (b ? { ...b, shots: data.shots } : b));
        setSavedAt(Date.now());
        setScenes((prev) => prev.map((s) => (s.scene_id === selectedId ? { ...s, status: "ready", shot_count: data.shots.length } : s)));
      } catch { toast.error("Couldn't save the shot list."); }
      finally { setSaving(false); }
    }, 900);
  };

  const mutateShots = (fn) => {
    setBoard((b) => {
      if (!b) return b;
      const shots = fn([...(b.shots || [])]).map((s, i) => ({ ...s, letter: shotLetter(i) }));
      scheduleSave(shots);
      return { ...b, shots };
    });
  };

  const updateShot = (idx, patch) => mutateShots((shots) => { shots[idx] = { ...shots[idx], ...patch }; return shots; });
  const removeShot = async (idx) => {
    const ok = await confirm({ title: "Delete this shot?", confirmLabel: "Delete", tone: "danger" });
    if (ok) mutateShots((shots) => { shots.splice(idx, 1); return shots; });
  };
  const moveShot = (idx, dir) => mutateShots((shots) => {
    const j = idx + dir;
    if (j < 0 || j >= shots.length) return shots;
    [shots[idx], shots[j]] = [shots[j], shots[idx]];
    return shots;
  });
  const addShot = () => mutateShots((shots) => {
    shots.push({ id: crypto.randomUUID(), letter: "A", shot_type: "MS", angle: "", camera_move: "STATIC",
      characters: [], action: "", dialogue: "", frame: "", panel_status: "none", panel_url: "" });
    return shots;
  });

  const exportPdf = () => {
    setExportOpen(false);
    downloadFile(`/scripts/${scriptId}/storyboard/export`, `${scriptTitle || "Storyboard"} - Shot List.pdf`)
      .catch(() => toast.error("Nothing to export yet — generate a shot list first."));
  };

  const exportPanelsPdf = () => {
    setExportOpen(false);
    toast("Building your storyboard PDF…");
    downloadFile(`/scripts/${scriptId}/storyboard/export-panels`, `${scriptTitle || "Storyboard"} - Storyboard.pdf`)
      .catch(() => toast.error("Nothing to export yet — generate a shot list first."));
  };

  const toggleView = () => {
    setView((v) => {
      const next = v === "list" ? "grid" : "list";
      try { localStorage.setItem("screenplay_sb_view", next); } catch { /* ignore */ }
      return next;
    });
  };

  const renderViewToggle = () => (
    <button
      onClick={toggleView}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
      data-testid="storyboard-view-toggle"
      title={view === "list" ? "Board view — panels in a presentation grid" : "List view — edit shots"}
    >
      {view === "list" ? <LayoutGrid size={13} /> : <List size={13} />}
      {view === "list" ? "Board" : "List"}
    </button>
  );

  const renderExportMenu = (compact) => (
    <div className="relative">
      <button
        onClick={() => setExportOpen((o) => !o)}
        disabled={!anyReady}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] disabled:opacity-50"
        data-testid="storyboard-export-btn"
      >
        <FileDown size={13} /> {compact ? "PDF" : "Export PDF"} <ChevronDown size={11} />
      </button>
      {exportOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setExportOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-[var(--line)] rounded-md shadow-lg min-w-[190px] py-1" data-testid="storyboard-export-menu">
            <button onClick={exportPanelsPdf} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--warm-accent)]" data-testid="storyboard-export-panels-btn">
              <span className="font-medium text-[var(--ink-primary)]">Storyboard PDF</span>
              <span className="block text-[10px] text-[var(--ink-muted)]">Panels in 2×3 grid pages</span>
            </button>
            <button onClick={exportPdf} className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--warm-accent)]" data-testid="storyboard-export-shotlist-btn">
              <span className="font-medium text-[var(--ink-primary)]">Shot list PDF</span>
              <span className="block text-[10px] text-[var(--ink-muted)]">Text-only shooting document</span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  const selectedScene = scenes.find((s) => s.scene_id === selectedId);
  const anyReady = scenes.some((s) => s.status === "ready" && s.shot_count > 0);
  const isGenerating = board?.status === "generating";
  const hasShots = board?.status === "ready" && (board.shots || []).length > 0;

  return (
    <div className="flex h-full min-h-0" data-testid="storyboard-view">
      {/* Scene rail (desktop) */}
      {!isMobile && (
      <aside className="w-64 shrink-0 border-r border-[var(--line)] bg-[var(--sidebar-bg)] flex flex-col">
        <div className="px-4 h-12 flex items-center justify-between border-b border-[var(--line)] shrink-0">
          <span className="font-display text-base text-[var(--ink-primary)] flex items-center gap-2">
            <Clapperboard size={16} strokeWidth={1.5} className="text-[var(--brand)]" /> Storyboard
          </span>
          <button
            onClick={() => setCastOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
            data-testid="storyboard-cast-btn"
            title="Character portraits that keep faces consistent across panels"
          >
            <Users size={12} /> Cast
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2" data-testid="storyboard-scene-list">
          {scenesLoading && <div className="px-4 py-3 text-sm text-[var(--ink-muted)]">Loading scenes…</div>}
          {!scenesLoading && scenes.length === 0 && (
            <div className="px-4 py-3 text-sm text-[var(--ink-muted)]">No scenes yet — add a scene heading to your script first.</div>
          )}
          {scenes.map((s) => (
            <button
              key={s.scene_id}
              onClick={() => setSelectedId(s.scene_id)}
              className={`w-full text-left px-4 py-2.5 hover:bg-[var(--warm-accent)] transition-colors ${selectedId === s.scene_id ? "bg-[var(--brand-soft)]" : ""}`}
              data-testid={`storyboard-scene-item-${s.number}`}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] font-semibold text-[var(--ink-muted)] shrink-0 w-6">{s.number}</span>
                <span className="text-xs text-[var(--ink-primary)] truncate flex-1">{s.heading}</span>
              </div>
              <div className="pl-8 text-[10px] mt-0.5">
                {s.status === "generating" ? (
                  <span className="text-[var(--brand)] inline-flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> listing shots…</span>
                ) : s.shot_count > 0 ? (
                  <span className="text-[var(--ink-muted)]">{s.shot_count} shots</span>
                ) : s.status === "error" ? (
                  <span className="text-[var(--err)]">failed</span>
                ) : (
                  <span className="text-[var(--ink-muted)] opacity-60">not boarded</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {isMobile ? (
          <div className="px-3 py-2 border-b border-[var(--line)] bg-[var(--paper)] shrink-0 space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={selectedId || ""}
                onChange={(e) => setSelectedId(e.target.value)}
                className="flex-1 min-w-0 text-sm bg-white border border-[var(--line)] rounded px-2 py-1.5 outline-none focus:border-[var(--brand)]"
                data-testid="storyboard-scene-select"
              >
                {scenes.length === 0 && <option value="">No scenes yet</option>}
                {scenes.map((s) => (
                  <option key={s.scene_id} value={s.scene_id}>
                    {s.number}. {s.heading}{s.shot_count > 0 ? ` · ${s.shot_count} shots` : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setCastOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] shrink-0"
                data-testid="storyboard-cast-btn"
              >
                <Users size={13} /> Cast
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {canEdit && hasShots && view === "list" && (
                <button onClick={generatePanels} className="btn-brand text-xs inline-flex items-center gap-1.5 px-3 py-1.5" data-testid="storyboard-panels-btn">
                  <ImagePlus size={13} /> Draw panels
                </button>
              )}
              {canEdit && selectedScene && view === "list" && (
                <button
                  onClick={generate}
                  disabled={isGenerating}
                  className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-60 ${hasShots ? "rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]" : "btn-brand"}`}
                  data-testid="storyboard-generate-btn"
                >
                  {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  {hasShots ? "Regenerate" : "Generate shot list"}
                </button>
              )}
              {renderViewToggle()}
              {renderExportMenu(true)}
              <span className="text-[11px] text-[var(--ink-muted)] ml-auto" data-testid="storyboard-saving">
                {saving ? "Saving…" : savedAt ? "Saved ✓" : ""}
              </span>
            </div>
          </div>
        ) : (
        <div className="h-12 px-5 flex items-center justify-between border-b border-[var(--line)] bg-[var(--paper)] shrink-0 gap-3">
          <div className="min-w-0 flex items-baseline gap-2">
            {selectedScene && (
              <>
                <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] shrink-0">Scene {selectedScene.number}</span>
                <span className="text-sm font-semibold text-[var(--ink-primary)] truncate" data-testid="storyboard-scene-heading">{selectedScene.heading}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-[var(--ink-muted)]" data-testid="storyboard-saving">
              {saving ? "Saving…" : savedAt ? "Saved ✓" : ""}
            </span>
            {canEdit && hasShots && view === "list" && (
              <button
                onClick={generatePanels}
                className="btn-brand text-xs inline-flex items-center gap-1.5 px-3 py-1.5"
                data-testid="storyboard-panels-btn"
                title="Draw B&W pencil panels for every shot that doesn't have one"
              >
                <ImagePlus size={13} /> Draw panels
              </button>
            )}
            {canEdit && selectedScene && view === "list" && (
              <button
                onClick={generate}
                disabled={isGenerating}
                className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-60 ${hasShots ? "rounded border border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]" : "btn-brand"}`}
                data-testid="storyboard-generate-btn"
              >
                {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                {hasShots ? "Regenerate shots" : "Generate shot list"}
              </button>
            )}
            {renderViewToggle()}
            {renderExportMenu(false)}
          </div>
        </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 sm:py-5" data-testid="storyboard-body">
          {boardLoading && (
            <div className="h-full flex items-center justify-center text-sm text-[var(--ink-muted)]">
              <Loader2 size={15} className="animate-spin mr-2" /> Loading…
            </div>
          )}

          {!boardLoading && isGenerating && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Loader2 size={26} className="animate-spin text-[var(--brand)]" />
              <p className="font-display text-lg text-[var(--ink-primary)] mt-4">Breaking the scene into shots…</p>
              <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-sm">A director's pass: coverage, angles, and camera moves that serve the scene's turn. Usually under a minute.</p>
            </div>
          )}

          {!boardLoading && !isGenerating && board?.status === "error" && (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="storyboard-error">
              <AlertTriangle size={24} className="text-[var(--err)]" />
              <p className="text-sm text-[var(--ink-secondary)] mt-3">{board.error || "That didn't work. Try again."}</p>
              {canEdit && <button onClick={generate} className="btn-brand text-sm mt-4 px-4 py-2">Try again</button>}
            </div>
          )}

          {!boardLoading && !isGenerating && (!board || board.status === "none" || (board.status === "ready" && !(board.shots || []).length)) && selectedScene && (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto">
              <div className="w-12 h-12 rounded-full bg-[var(--brand-soft)] flex items-center justify-center">
                <Clapperboard size={22} className="text-[var(--brand)]" />
              </div>
              <p className="font-display text-xl text-[var(--ink-primary)] mt-4">Board this scene</p>
              <p className="text-sm text-[var(--ink-secondary)] mt-2 leading-relaxed">
                A professional shot list gets drafted from the scene — shot types, angles, and camera
                moves — then pencil panels bring it to life.
              </p>
              {canEdit && (
                <button onClick={generate} className="btn-brand text-sm mt-5 px-5 py-2.5 inline-flex items-center gap-2" data-testid="storyboard-empty-generate-btn">
                  <Clapperboard size={15} /> Generate shot list
                </button>
              )}
            </div>
          )}

          {!boardLoading && !isGenerating && hasShots && view === "grid" && (
            <GridBoard
              shots={board.shots}
              sceneNumber={selectedScene?.number}
              scriptId={scriptId}
              onZoom={(src, caption) => setLightbox({ src, caption })}
            />
          )}

          {!boardLoading && !isGenerating && hasShots && view === "list" && (
            <div className="max-w-3xl mx-auto space-y-4 pb-10">
              {board.shots.map((s, i) => (
                <ShotCard
                  key={s.id}
                  shot={s}
                  index={i}
                  total={board.shots.length}
                  sceneNumber={selectedScene?.number}
                  scriptId={scriptId}
                  canEdit={canEdit}
                  onChange={(patch) => updateShot(i, patch)}
                  onDelete={() => removeShot(i)}
                  onMove={(dir) => moveShot(i, dir)}
                  onRegen={(note) => regenPanel(s.id, note)}
                  onZoom={(src, caption) => setLightbox({ src, caption })}
                />
              ))}
              {canEdit && (
                <button
                  onClick={addShot}
                  className="w-full flex items-center justify-center gap-2 px-3 py-3 text-xs uppercase tracking-wider rounded-lg border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
                  data-testid="shot-add-btn"
                >
                  <Plus size={14} /> Add shot
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {castOpen && <CastDialog scriptId={scriptId} canEdit={canEdit} onClose={() => setCastOpen(false)} />}

      {lightbox && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" onClick={() => setLightbox(null)} data-testid="storyboard-lightbox">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.caption} className="w-full rounded-lg shadow-2xl bg-white" />
            <div className="flex items-center justify-between mt-2">
              <span className="text-white/90 text-sm font-mono-script">{lightbox.caption}</span>
              <button onClick={() => setLightbox(null)} className="text-white/70 hover:text-white p-1" data-testid="storyboard-lightbox-close"><X size={18} /></button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ShotCard({ shot, index, total, sceneNumber, scriptId, canEdit, onChange, onDelete, onMove, onRegen, onZoom }) {
  const [tweakOpen, setTweakOpen] = useState(false);
  const [tweak, setTweak] = useState("");
  const selCls = "text-xs bg-white border border-[var(--line)] rounded px-1.5 py-1 outline-none focus:border-[var(--brand)] disabled:opacity-60";
  const shotNo = `${sceneNumber}${shot.letter}`;
  const panelReady = shot.panel_status === "ready" && shot.panel_url;

  const sendRegen = () => {
    onRegen(tweak);
    setTweak("");
    setTweakOpen(false);
  };

  return (
    <div className="rounded-lg border border-[var(--line)] bg-[var(--paper)] overflow-hidden" data-testid={`shot-card-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--line)] bg-[var(--warm-accent)]/40 flex-wrap">
        <span className="font-mono-script text-sm font-bold text-[var(--brand)] shrink-0 w-12" data-testid={`shot-number-${index}`}>
          {shotNo}
        </span>
        <select value={shot.shot_type} disabled={!canEdit} onChange={(e) => onChange({ shot_type: e.target.value })} className={selCls} data-testid={`shot-type-select-${index}`} title="Shot type">
          {SHOT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={shot.camera_move} disabled={!canEdit} onChange={(e) => onChange({ camera_move: e.target.value })} className={selCls} data-testid={`shot-move-select-${index}`} title="Camera move">
          {CAMERA_MOVES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          value={shot.angle}
          disabled={!canEdit}
          onChange={(e) => onChange({ angle: e.target.value })}
          placeholder="angle"
          className={`${selCls} w-24`}
          data-testid={`shot-angle-input-${index}`}
          title="Camera angle (eye level, low, high…)"
        />
        <input
          value={(shot.characters || []).join(", ")}
          disabled={!canEdit}
          onChange={(e) => onChange({ characters: e.target.value.split(",").map((c) => c.trim()).filter(Boolean) })}
          placeholder="characters in frame"
          className={`${selCls} flex-1 min-w-[120px]`}
          data-testid={`shot-characters-input-${index}`}
        />
        {canEdit && (
          <span className="flex items-center gap-0.5 shrink-0 ml-auto">
            <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] disabled:opacity-30" data-testid={`shot-up-${index}`} title="Move up"><ChevronUp size={14} /></button>
            <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] disabled:opacity-30" data-testid={`shot-down-${index}`} title="Move down"><ChevronDown size={14} /></button>
            <button onClick={onDelete} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] hover:text-[var(--err)]" data-testid={`shot-delete-${index}`} title="Delete shot"><Trash2 size={13} /></button>
          </span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-3">
        {/* 16:9 panel */}
        <div className="w-full sm:w-56 shrink-0">
          <div className="aspect-video rounded border border-[var(--line)] overflow-hidden relative group bg-[var(--warm-accent)]/30" data-testid={`shot-panel-${index}`}>
            {panelReady ? (
              <img
                src={imgSrc(scriptId, shot.panel_url)}
                alt={`Panel ${shotNo}`}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => onZoom(imgSrc(scriptId, shot.panel_url), `${shotNo} · ${shot.shot_type} · ${shot.camera_move}`)}
                data-testid={`shot-panel-img-${index}`}
              />
            ) : shot.panel_status === "generating" ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--brand)]" data-testid={`shot-panel-generating-${index}`}>
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[10px] mt-1.5 text-[var(--ink-muted)]">Drawing…</span>
              </div>
            ) : shot.panel_status === "error" ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--err)]">
                <AlertTriangle size={16} />
                <span className="text-[10px] mt-1 opacity-80">Didn't draw</span>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--ink-muted)] border border-dashed border-[var(--line-strong)] rounded">
                <ImageOff size={16} strokeWidth={1.5} className="opacity-50" />
                <span className="text-[10px] mt-1 opacity-70">No panel yet</span>
              </div>
            )}
          </div>
          {canEdit && shot.panel_status !== "generating" && (
            <div className="mt-1.5">
              {!tweakOpen ? (
                <button
                  onClick={() => setTweakOpen(true)}
                  className="text-[10px] text-[var(--ink-muted)] hover:text-[var(--brand)] inline-flex items-center gap-1"
                  data-testid={`shot-panel-regen-btn-${index}`}
                >
                  <RefreshCw size={10} /> {panelReady ? "Redraw panel" : "Draw this panel"}
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={tweak}
                    onChange={(e) => setTweak(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") sendRegen(); if (e.key === "Escape") setTweakOpen(false); }}
                    placeholder="optional note — 'lower angle'"
                    className="flex-1 text-[10px] bg-white border border-[var(--line)] rounded px-1.5 py-1 outline-none focus:border-[var(--brand)]"
                    data-testid={`shot-panel-tweak-input-${index}`}
                  />
                  <button onClick={sendRegen} className="text-[10px] text-[var(--brand)] hover:underline shrink-0" data-testid={`shot-panel-tweak-go-${index}`}>Draw</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[var(--ink-muted)]">Action</label>
            <textarea
              value={shot.action}
              disabled={!canEdit}
              onChange={(e) => onChange({ action: e.target.value })}
              rows={2}
              className="w-full text-[13px] leading-relaxed bg-transparent border border-transparent hover:border-[var(--line)] focus:border-[var(--brand)] focus:bg-white rounded px-2 py-1 outline-none resize-y text-[var(--ink-primary)] disabled:opacity-70"
              data-testid={`shot-action-input-${index}`}
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[var(--ink-muted)]">Dialogue</label>
            <textarea
              value={shot.dialogue}
              disabled={!canEdit}
              onChange={(e) => onChange({ dialogue: e.target.value })}
              rows={1}
              placeholder="—"
              className="w-full text-[13px] italic leading-relaxed bg-transparent border border-transparent hover:border-[var(--line)] focus:border-[var(--brand)] focus:bg-white rounded px-2 py-1 outline-none resize-y text-[var(--ink-secondary)] disabled:opacity-70"
              data-testid={`shot-dialogue-input-${index}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function GridBoard({ shots, sceneNumber, scriptId, onZoom }) {
  return (
    <div className="max-w-5xl mx-auto pb-10" data-testid="storyboard-grid">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {shots.map((s, i) => {
          const shotNo = `${sceneNumber}${s.letter}`;
          const ready = s.panel_status === "ready" && s.panel_url;
          return (
            <figure key={s.id} className="bg-white rounded-lg border border-[var(--line)] shadow-sm overflow-hidden" data-testid={`grid-panel-${i}`}>
              <div className="aspect-video bg-[var(--warm-accent)]/30 relative">
                {ready ? (
                  <img
                    src={imgSrc(scriptId, s.panel_url)}
                    alt={`Panel ${shotNo}`}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => onZoom(imgSrc(scriptId, s.panel_url), `${shotNo} · ${s.shot_type} · ${s.camera_move}`)}
                    data-testid={`grid-panel-img-${i}`}
                  />
                ) : s.panel_status === "generating" ? (
                  <div className="w-full h-full flex items-center justify-center text-[var(--brand)]"><Loader2 size={18} className="animate-spin" /></div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[var(--ink-muted)]">
                    <ImageOff size={16} strokeWidth={1.5} className="opacity-40" />
                    <span className="text-[10px] mt-1 opacity-60">No panel</span>
                  </div>
                )}
              </div>
              <figcaption className="px-3 py-2.5">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="font-mono-script font-bold text-[var(--brand)]">{shotNo}</span>
                  <span className="uppercase tracking-wide text-[var(--ink-secondary)] font-medium">{s.shot_type}</span>
                  {s.angle && <span className="text-[var(--ink-muted)]">{s.angle}</span>}
                  {s.camera_move && s.camera_move !== "STATIC" && (
                    <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full border border-[var(--line)] uppercase tracking-wider text-[var(--ink-secondary)]">{s.camera_move}</span>
                  )}
                </div>
                {s.action && <p className="text-xs text-[var(--ink-primary)] leading-relaxed mt-1.5 line-clamp-3">{s.action}</p>}
                {s.dialogue && <p className="text-xs italic text-[var(--ink-muted)] leading-relaxed mt-1 line-clamp-2">“{s.dialogue}”</p>}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}

function CastDialog({ scriptId, canEdit, onClose }) {
  const [cast, setCast] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/storyboard/characters`);
      setCast(data.cast || []);
      const busy = (data.cast || []).some((c) => c.status === "generating");
      if (busy && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          const { data: d } = await api.get(`/scripts/${scriptId}/storyboard/characters`);
          setCast(d.cast || []);
          if (!(d.cast || []).some((c) => c.status === "generating")) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 4500);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [scriptId]);

  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [load]);

  const generate = async (characterId) => {
    try {
      await api.post(`/scripts/${scriptId}/storyboard/characters/${characterId}/generate`);
      setCast((prev) => prev.map((c) => (c.character_id === characterId ? { ...c, status: "generating" } : c)));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the portrait.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="cast-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-[var(--paper)] rounded-xl shadow-2xl border border-[var(--line)] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] shrink-0">
          <div>
            <h2 className="font-display text-lg leading-none text-[var(--ink-primary)] flex items-center gap-2">
              <Users size={17} className="text-[var(--brand)]" /> Cast
            </h2>
            <p className="text-[11px] text-[var(--ink-muted)] mt-1">
              Pencil portraits that anchor each character's look — panels use them as references so faces stay consistent.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="cast-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-sm text-[var(--ink-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {!loading && cast.length === 0 && (
            <div className="text-sm text-[var(--ink-muted)] text-center py-8">
              No characters yet — add them in the Characters section of the Resources sidebar first.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {cast.map((c) => (
              <div key={c.character_id} className="rounded-lg border border-[var(--line)] overflow-hidden" data-testid={`cast-card-${c.character_id}`}>
                <div className="aspect-square bg-[var(--warm-accent)]/30 relative">
                  {c.status === "ready" && c.image_id ? (
                    <img src={imgSrc(scriptId, c.image_id)} alt={c.name} className="w-full h-full object-cover" data-testid={`cast-img-${c.character_id}`} />
                  ) : c.status === "generating" ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[var(--brand)]">
                      <Loader2 size={18} className="animate-spin" />
                      <span className="text-[10px] mt-1.5 text-[var(--ink-muted)]">Drawing…</span>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[var(--ink-muted)]">
                      <User size={28} strokeWidth={1} className="opacity-40" />
                    </div>
                  )}
                </div>
                <div className="px-2.5 py-2">
                  <div className="text-xs font-semibold text-[var(--ink-primary)] truncate">{c.name}</div>
                  {canEdit && c.status !== "generating" && (
                    <button
                      onClick={() => generate(c.character_id)}
                      className="mt-1 text-[10px] text-[var(--brand)] hover:underline inline-flex items-center gap-1"
                      data-testid={`cast-generate-${c.character_id}`}
                    >
                      <RefreshCw size={10} /> {c.status === "ready" ? "Redraw" : "Draw portrait"}
                    </button>
                  )}
                  {c.status === "error" && <div className="text-[10px] text-[var(--err)] mt-0.5">Didn't draw — try again</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
